import type Cropper from "cropperjs";

export const MAX_ZOOM = 3;

// HP-9217: the single source of truth for every cropped-canvas export. Uncapped, cropperjs
// rasterizes at full native camera resolution (~12MP on a phone) and does it synchronously, so the
// UI freezes before toBlob starts encoding. These caps lived only in ChunkUploadFile for years and
// were never propagated -- keeping one exported object is what stops that happening again.
export const CROPPED_CANVAS_OPTIONS = {
  maxWidth: 2048,
  maxHeight: 2048,
  imageSmoothingEnabled: true,
  imageSmoothingQuality: "high"
} as const;

export const ASPECT_RATIOS = [
  { label: "Free", value: Number.NaN },
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 }
] as const;

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const hasImageData = (cropper?: Cropper) => {
  const image = cropper?.getImageData();
  return Boolean(image?.naturalWidth && image?.naturalHeight && image?.width && image?.height);
};

export const safeZoomTo = (cropper: Cropper | undefined, ratio: number, tries = 8) => {
  if (!cropper) return;
  if (hasImageData(cropper)) {
    cropper.zoomTo(ratio);
    return;
  }
  if (tries > 0) {
    window.setTimeout(() => safeZoomTo(cropper, ratio, tries - 1), 16);
  }
};

export const getFitZoomRatio = (cropper?: Cropper) => {
  const container = cropper?.getContainerData();
  const image = cropper?.getImageData();
  if (!container || !image?.naturalWidth || !image?.naturalHeight) return 0.1;

  const rotation = (((image.rotate || 0) % 360) + 360) % 360;
  const sideways = rotation === 90 || rotation === 270;
  const width = sideways ? image.naturalHeight : image.naturalWidth;
  const height = sideways ? image.naturalWidth : image.naturalHeight;

  return Math.min(container.width / width, container.height / height);
};

export const getCurrentZoomRatio = (cropper?: Cropper) => {
  const image = cropper?.getImageData();
  return image?.naturalWidth ? image.width / image.naturalWidth : 1;
};

export const maximizeCropBox = (cropper: Cropper | undefined, aspectRatio: number) => {
  if (!cropper) return;
  const canvas = cropper.getCanvasData();
  if (!canvas.width || !canvas.height) return;

  let width = canvas.width;
  let height = canvas.height;

  if (Number.isFinite(aspectRatio)) {
    height = width / aspectRatio;
    if (height > canvas.height) {
      height = canvas.height;
      width = height * aspectRatio;
    }
  }

  cropper.setCropBoxData({
    left: canvas.left + (canvas.width - width) / 2,
    top: canvas.top + (canvas.height - height) / 2,
    width,
    height
  });
  cropper.crop();
};
